/**
 * Creating a reference-data schema without writing JSON (ADR-127 D2).
 *
 * This is the part of the Data area where nothing existed. Every schema in the fleet so far —
 * `website-property`, `asana-section`, `asana-custom-field`, `asana-authorized-director` — was
 * hand-authored JSON, which is exactly what ADR-125 D12 says should not be necessary.
 *
 * THREE THINGS IT REFUSES TO DECIDE, ON PURPOSE
 * --------------------------------------------
 *  1. WHETHER THE DRAFT IS VALID. That is the canonical Python validator's answer, fetched from
 *     `/api/data-references/validate-draft` and staged over the real registry so an `$id`
 *     collision and the live-version cap can actually fire. A second opinion computed in the
 *     browser is the drift ADR-129 D1 exists to prevent — and any rule it failed to carry would
 *     surface only as a red check on the PR.
 *  2. WHO OWNS IT. There is no core/fleet picker: ADR-125 D10 says core never prescribes a
 *     fleet's operational values, and D3 puts core artifacts in a namespace this surface cannot
 *     write to. Offering the choice would be offering one that cannot be saved.
 *  3. WHAT THE LAYER-2 VOCABULARY IS. Six keywords, closed (ADR-129 D4). The list is read out of
 *     the canonical validator, not retyped from prose — a seventh is a core schema-engine
 *     change, not a form option.
 *
 * The pair of documents is proposed as ONE changeset (ADR-134 D1), because a schema with no data
 * document is valid to CI but invisible to the Data area, so proposing them separately produces
 * a PR whose result the operator cannot see.
 */

import { useRef, useState } from 'react'

import { api, dataFindingLine, type DataDraftVerdict, type ProposeSkillResult } from '../api/client'
import { serializeDocument } from './dataDocumentEdit'
import { useGuardedSpec } from './useGuardedSpec'
import {
  pluralise,
  collectionFor,
  generateDraft,
  type DraftField, type FieldType, type SchemaDraftSpec, slugifyTyping, schemaProblems, recordProblems,
  leafPaths, readPath, writePath,
} from './schemaDraft'

// `object` last: it is the one that changes the SHAPE of the record rather than
// the type of a value, so it reads as the deliberate choice it is.
const TYPES: FieldType[] = ['string', 'number', 'integer', 'boolean', 'object']

const EMPTY: SchemaDraftSpec = {
  name: '', setName: '', version: 1, title: '', description: '',
  collection: '', keyField: 'key', minItems: undefined, maxItems: 32,
  provenanceFields: [], fields: [{ name: 'key', type: 'string', required: true }],
  rows: [{}],
}

export interface SchemaBuilderProps {
  canPropose: boolean
  onClose: () => void
  onCreated: (setRef: string) => void
}

export function SchemaBuilder({ canPropose, onClose, onCreated }: SchemaBuilderProps) {
  /**
   * The verdict AND the paths it was computed for, as ONE value written in one go.
   *
   * The findings split below asks whose file each finding is about, and that question is only
   * answerable against the paths that were STAGED. Held apart, the two drift the moment an edit
   * lands between the request and the response: the verdict describes the paths the check sent,
   * the split compares them against the paths the form now has, and a finding about the
   * operator's OWN draft is filed under the registry heading — the one that says "Nothing on this
   * form caused it". Stored together they cannot disagree.
   */
  const [checked, setChecked] = useState<{ verdict: DataDraftVerdict; paths: string[] } | null>(null)
  const verdict = checked?.verdict ?? null
  /**
   * Which check the operator is actually waiting on.
   *
   * Every edit clears the verdict, but nothing cancels a request already in flight — so its
   * response would repopulate the verdict the edit deliberately cleared, with an answer about a
   * draft that no longer exists. Bumped by every clear and by every new check, this makes the
   * late response a no-op.
   */
  const checkRun = useRef(0)
  /**
   * The PROPOSAL, once it exists.
   *
   * Creating used to call onCreated() straight away, which closes this panel — so
   * the PR number and the review link were produced and then thrown away, and the
   * gesture ended in silence. Nothing had been applied at that point (ADR-122:
   * Approve decides, Apply ships), so an operator who did not already know that
   * had no way to learn what to do next. The panel now ends on the call to
   * action and hands control back only when it is dismissed.
   */
  const [created, setCreated] = useState<ProposeSkillResult | null>(null)
  const [unchecked, setUnchecked] = useState<string | null>(null)
  const [checking, setChecking] = useState(false)
  const [proposing, setProposing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // A verdict about a draft the operator has since edited is a stale verdict, and a stale pass
  // is the dangerous kind — so any edit invalidates it, INCLUDING one still in flight.
  const clearVerdict = () => {
    checkRun.current += 1
    setChecked(null)
    setUnchecked(null)
  }
  /**
   * The draft, and the ONLY two ways to change it.
   *
   * There is no raw setter in this scope on purpose — see `useGuardedSpec`. The rule above used
   * to be a second call each edit site had to remember, and the two field controls written last
   * did not.
   */
  const { spec, updateSpec, patch } = useGuardedSpec<SchemaDraftSpec>(EMPTY, clearVerdict)
  // Which DERIVED names the operator has taken over.
  //
  // Tracked rather than inferred from the value, because "the set name happens to equal the
  // plural" and "the operator typed that plural themselves" look identical in the state and must
  // behave differently: the first keeps following the schema name, the second must never be
  // overwritten again. Inferring it would silently revert a deliberate edit the moment the
  // schema name changed.
  const [ownedNames, setOwnedNames] = useState<{ setName: boolean; collection: boolean }>({
    setName: false, collection: false,
  })
  const [advancedOpen, setAdvancedOpen] = useState(false)

  // Split, because first records cannot be judged against a shape that is not yet
  // valid: with an unresolved key field every row looks wrong, and the operator is
  // sent to fix rows when the actual fault is above them.
  // The record's scalar leaves, as dotted paths — the vocabulary the key field and
  // the first-records columns both speak.
  const leaves = leafPaths(spec.fields)
  const shapeProblems = schemaProblems(spec)
  const shapeReady = shapeProblems.length === 0
  const problems = shapeReady ? [...shapeProblems, ...recordProblems(spec)] : shapeProblems
  const wellFormed = problems.length === 0
  const draft = wellFormed ? generateDraft(spec) : null
  /**
   * Typing the schema name carries the two derived names along, until the operator takes one over.
   *
   * The set name and the collection property are mechanical in every set the registry contains
   * (see `pluralise`/`collectionFor`), so they follow rather than being asked for. They remain
   * REAL fields with real values — nothing is computed at generate time — because they are
   * separate identities the operator is allowed to diverge, and a value that only appears at the
   * last moment is one nobody can check before creating.
   */
  const patchSchemaName = (raw: string) => {
    // The convention is ours, so the transform is ours (see slugify). An operator
    // who types `fleet_short_codes` has said exactly what they mean.
    const name = slugifyTyping(raw)
    const next: Partial<SchemaDraftSpec> = { name }
    if (!ownedNames.setName) {
      const setName = pluralise(name)
      next.setName = setName
      // The collection follows the SET name, so it re-derives here too unless owned — otherwise
      // renaming the schema would leave a collection named after the previous one.
      if (!ownedNames.collection) next.collection = collectionFor(setName)
    }
    patch(next)
  }

  const patchSetName = (raw: string) => {
    const setName = slugifyTyping(raw)
    setOwnedNames(o => ({ ...o, setName: true }))
    patch(ownedNames.collection
      ? { setName }
      : { setName, collection: collectionFor(setName) })
  }

/**
 * x-key must always name a field that EXISTS.
 *
 * A <select> whose `value` matches no <option> renders the first option while
 * the bound state keeps the stale value — so the form silently disagreed with
 * itself. Keeping the state reconciled means the control can never display a
 * choice the draft has not actually made.
 */
function reconcileKeyField(current: string, fields: DraftField[]): string {
  // Over LEAVES, not top-level fields: an object is not a value, so `address` can
  // never be the key while `address.city` can.
  const named = leafPaths(fields)
  if (named.includes(current)) return current
  return named[0] ?? ''
}

/** Apply `fn` to the field at `path`, rebuilding only the nodes on the way down. */
function mapFieldAt(
  fields: DraftField[], path: number[], fn: (f: DraftField) => DraftField | null,
): DraftField[] {
  const [head, ...rest] = path
  return fields.flatMap((f, i) => {
    if (i !== head) return [f]
    if (rest.length === 0) {
      const next = fn(f)
      return next ? [next] : []
    }
    return [{ ...f, fields: mapFieldAt(f.fields ?? [], rest, fn) }]
  })
}

/** The declared type of a scalar leaf, so its input can be typed. */
function leafType(fields: DraftField[], path: string): FieldType {
  const [head, ...rest] = path.split('.')
  const field = fields.find(f => f.name === head)
  if (!field) return 'string'
  if (rest.length === 0) return field.type
  return leafType(field.fields ?? [], rest.join('.'))
}

  const patchRow = (i: number, path: string, value: unknown) => {
    updateSpec(s => ({
      ...s,
      // A dotted path, so `address.city` lands NESTED rather than as a literal key
      // called "address.city", which would validate against nothing.
      rows: s.rows.map((r, j) => (j === i ? writePath(r, path, value) : r)),
    }))
  }

  const check = async (): Promise<DataDraftVerdict | null> => {
    if (!draft) return null
    // The paths this request is ABOUT, read before the await so the verdict can be stored with
    // them, and the run they belong to, so a response the form has moved past can be recognised.
    const paths = [draft.schemaPath, draft.dataPath]
    checkRun.current += 1
    const run = checkRun.current
    setChecking(true)
    setError(null)
    try {
      const v = await api.validateDataDraft({
        schema_path: draft.schemaPath, data_path: draft.dataPath,
        schema: draft.schema, data: draft.data,
      })
      // The draft changed while this was out. The answer describes something the operator is no
      // longer looking at, and the edit cleared the verdict on purpose — so it is dropped, not
      // shown under a heading that would now name the wrong file.
      if (run !== checkRun.current) return null
      if (v.findings === undefined) {
        // The validator could not run. NOT "the draft is valid".
        setChecked(null)
        setUnchecked(v.error ?? 'the validator returned no findings array')
        return null
      }
      setChecked({ verdict: v, paths })
      setUnchecked(null)
      return v
    } catch (err) {
      if (run !== checkRun.current) return null
      setChecked(null)
      setUnchecked(err instanceof Error ? err.message : String(err))
      return null
    } finally {
      setChecking(false)
    }
  }

  const propose = async () => {
    if (!draft) return
    // Create RUNS the check rather than requiring the operator to have run it.
    //
    // The rule is unchanged and non-negotiable (ADR-129 D7): nothing is proposed
    // unless the canonical validator has accepted this exact draft, because a
    // published version is immutable. What changes is who performs the step. A
    // Create button that sits disabled on a complete, valid-looking form — with
    // no text saying "press Check first" — reads as broken, and the operator's
    // only recourse is to guess. The verdict is cleared by every edit, so this
    // can never propose against a stale pass.
    if (verdict?.valid !== true) {
      const v = await check()
      if (v?.valid !== true) return
    }
    setProposing(true)
    setError(null)
    try {
      const result = await api.proposeFleetChangeset(
        [
          { path: draft.schemaPath, content: serializeDocument(draft.schema) },
          { path: draft.dataPath, content: serializeDocument(draft.data) },
        ],
        `data(${spec.setName}@v${spec.version}): new reference-data set via the Data area`)
      setCreated(result)
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setProposing(false)
    }
  }

  // Enabled once the draft is WELL-FORMED. The canonical verdict is still
  // required before anything is proposed — propose() obtains it — so the
  // fail-closed rule is intact while the button stops being a dead end.
  const canCreate = canPropose && wellFormed && !proposing && !checking

  // THE FINDINGS ARE SPLIT BY WHOSE FILE THEY ARE ABOUT.
  //
  // The endpoint stages this fleet's WHOLE registry and validates all of it, because the checks
  // that matter — the live-version cap, `$schema_ref` resolution — cannot fire against a one-file
  // tree. The consequence is that a file the operator never touched can refuse their draft, and
  // that is exactly what the reported incident was: a valid draft, and an invalid
  // `config/schemas/fleet-channel/v1.json` already in the registry. Presenting that under "the
  // validator rejects this draft" sends the operator to hunt for a fault in a form that has none,
  // and leaves them with no route — the dead end ADR-127 D1 exists to design against.
  //
  // Own-vs-other is decided by the TWO real paths this verdict was computed for — the same values
  // sent to the endpoint, kept beside it — so the split cannot disagree with what was staged, and
  // cannot be answered against a draft the validator never saw.
  const draftPaths = checked?.paths ?? []
  const findingLines = (verdict?.findings ?? []).flatMap((f, i) =>
    (f.errors ?? []).map((e, j) => ({ key: `${i}-${j}`, rel: f.rel ?? '', text: dataFindingLine(e) })))
  const draftFindings = findingLines.filter(l => draftPaths.includes(l.rel))
  const registryFindings = findingLines.filter(l => !draftPaths.includes(l.rel))

  /**
   * Field rows, recursively. An `object` field's children are rendered beneath it,
   * indented, and are fields in every other respect — same types, same required
   * flag, same removal. Nesting is arbitrary depth because everything downstream
   * already recurses (schema generation, the validator's `_descend`, the picker's
   * `schema_digest`); the only real limit is legibility.
   */
  const renderFieldRows = (fields: DraftField[], prefix: number[], depth: number): JSX.Element[] =>
    fields.flatMap((f, i) => {
      const path = [...prefix, i]
      const id = path.join('-')
      const rows: JSX.Element[] = [
                    <tr key={id} data-testid={`sb-field-${id}`}>
                      <td className="pr-2" style={{ paddingLeft: depth * 20 }}>
                        <input
                          value={f.name}
                          onChange={e => patchAt(path, { name: e.target.value })}
                          placeholder="name"
                          data-testid={`sb-field-name-${id}`}
                          className="w-full border border-gray-200 rounded px-2 py-1 text-sm font-mono"
                        />
                      </td>
                      <td className="pr-2">
                        <select
                          value={f.type}
                          onChange={e => patchAt(path, { type: e.target.value as FieldType })}
                          data-testid={`sb-field-type-${id}`}
                          className="border border-gray-200 rounded px-2 py-1 text-sm"
                        >
                          {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </td>
                      <td className="pr-2">
                        <label className="text-xs text-gray-600 inline-flex items-center gap-1">
                          <input
                            type="checkbox" checked={f.required}
                            onChange={e => patchAt(path, { required: e.target.checked })}
                            data-testid={`sb-field-required-${id}`}
                          />
                          required
                        </label>
                      </td>
                      <td className="pr-2">
                        <label className="text-xs text-gray-600 inline-flex items-center gap-1">
                          <input
                            type="checkbox"
                            checked={spec.provenanceFields.includes(f.name)}
                            onChange={e => patch({
                              provenanceFields: e.target.checked
                                ? [...spec.provenanceFields, f.name]
                                : spec.provenanceFields.filter(p => p !== f.name),
                            })}
                            data-testid={`sb-field-provenance-${id}`}
                          />
                          provenance
                        </label>
                      </td>
                      <td className="pr-2">
                        <input
                          value={f.unit ?? ''}
                          onChange={e => patchAt(path, { unit: e.target.value || undefined })}
                          placeholder="unit"
                          data-testid={`sb-field-unit-${id}`}
                          className="w-24 border border-gray-200 rounded px-2 py-1 text-xs"
                        />
                      </td>
                      <td>
                        {(depth > 0 || spec.fields.length > 1) && (
                          <button
                            type="button"
                            onClick={() => updateSpec(s2 => {
                              const fields = mapFieldAt(s2.fields, path, () => null)
                              return { ...s2, fields, keyField: reconcileKeyField(s2.keyField, fields) }
                            })}
                            data-testid={`sb-field-remove-${id}`}
                            className="text-xs text-gray-400 hover:text-red-700"
                          >
                            remove
                          </button>
                        )}
                      </td>
                    </tr>
      ]
      if (f.type === 'object') {
        rows.push(...renderFieldRows(f.fields ?? [], path, depth + 1))
        rows.push(
          <tr key={`${id}-add`}>
            <td colSpan={6} style={{ paddingLeft: (depth + 1) * 20 }}>
              <button
                type="button"
                onClick={() => updateSpec(s2 => ({
                  ...s2,
                  fields: mapFieldAt(s2.fields, path, o => ({
                    ...o,
                    fields: [...(o.fields ?? []), { name: '', type: 'string', required: false }],
                  })),
                }))}
                data-testid={`sb-field-add-child-${id}`}
                className="text-xs text-indigo-700 hover:underline"
              >
                ＋ add field inside {f.name || 'this object'}
              </button>
            </td>
          </tr>,
        )
      }
      return rows
    })

  const patchAt = (path: number[], next: Partial<DraftField>) => {
    updateSpec(s2 => {
      const fields = mapFieldAt(s2.fields, path, f => ({ ...f, ...next }))
      // Follow a rename or a type change: an object is not a value, so switching a
      // field TO object can strand x-key just as a rename can.
      const renamed = next.name !== undefined || next.type !== undefined
      const current = renamed ? reconcileKeyField(s2.keyField, fields) : s2.keyField
      return { ...s2, fields, keyField: current }
    })
  }

  // The gesture ENDS here, on a call to action. Nothing has run yet — ADR-122's
  // lifecycle is Proposed -> Ready -> Live, and Approve is a separate, human
  // decision — so finishing silently would leave the operator believing the set
  // exists. It does not exist until the PR merges and deploys.
  if (created) {
    return (
      <div className="border border-gray-200 rounded p-4" data-testid="schema-builder-created">
        <h2 className="text-base font-semibold text-gray-900 mb-2">
          {spec.setName}@v{spec.version} — sent for review
        </h2>
        <p className="text-sm text-gray-700 mb-3">
          <strong>Nothing runs until you approve it.</strong> The schema and its data document went
          together as one changeset; the set does not exist on this fleet until that merges and
          deploys.
        </p>
        <ul className="text-sm text-gray-700 space-y-1 mb-4">
          {!created.improvement_warning && (
            <li>
              Pull request{' '}
              <a
                href={created.url} target="_blank" rel="noreferrer"
                className="font-medium text-blue-700 underline" data-testid="sb-created-pr"
              >#{created.pr_number}</a>
            </li>
          )}
          {created.files.map(f => <li key={f}><code className="text-xs">{f}</code></li>)}
        </ul>
        {created.improvement_warning ? (
          <p className="text-sm text-amber-800 mb-4" data-testid="sb-created-warning">
            {created.improvement_warning}
          </p>
        ) : created.improvement_id ? (
          // A plain anchor, not <Link>. <Link> needs Router context, so the panel
          // would crash anywhere it is rendered without one — which CI proved by
          // failing on an existing test that renders this component bare. A
          // terminal call-to-action is not worth making the component
          // context-dependent, and a full navigation is fine for a step taken once.
          <a
            href={`/improvements/${encodeURIComponent(created.improvement_id)}/pr`}
            className="text-sm font-medium text-blue-700 underline"
            data-testid="sb-created-review"
          >Review &amp; approve in Mission Control →</a>
        ) : (
          <span className="text-sm text-gray-700" data-testid="sb-created-review">
            Find it under{' '}
            <a href="/improvements" className="font-medium text-blue-700 underline">
              Improvements
            </a>.
          </span>
        )}
        <div className="mt-4">
          <button
            type="button"
            onClick={() => onCreated(`${spec.setName}@v${spec.version}`)}
            data-testid="sb-created-done"
            className="px-3 py-1.5 text-sm rounded border border-gray-300"
          >
            Done
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="border border-gray-200 rounded p-4" data-testid="schema-builder">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-semibold text-gray-900">New schema</h2>
        <button type="button" onClick={onClose} className="text-xs text-gray-500 hover:underline">
          Cancel
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <label className="text-xs text-gray-700">
          Schema name
          <input
            value={spec.name}
            onChange={e => patchSchemaName(e.target.value)}
            placeholder="asana-authorized-director"
            data-testid="sb-name"
            className="mt-1 w-full border border-gray-200 rounded px-2 py-1 text-sm font-mono"
          />
        </label>
        <label className="text-xs text-gray-700">
          Set name{' '}
          <span className="text-gray-400">(the data file, and the default reference alias)</span>
          <input
            value={spec.setName}
            onChange={e => patchSetName(e.target.value)}
            placeholder="asana-authorized-directors"
            data-testid="sb-setname"
            className="mt-1 w-full border border-gray-200 rounded px-2 py-1 text-sm font-mono"
          />
          {/* Worth one line, because this is the ONE derived name an author will read back: the
              offers endpoint suggests the set name as the alias, so a reference reads
              `{{data.asana-sections.by_key.…}}`. */}
          <span className="text-[10px] text-gray-400">
            Follows the schema name until you change it.
          </span>
        </label>
        <label className="text-xs text-gray-700">
          Key field <span className="text-gray-400">(x-key — what consumers spell)</span>
          <select
            value={spec.keyField}
            onChange={e => patch({ keyField: e.target.value })}
            data-testid="sb-keyfield"
            className="mt-1 w-full border border-gray-200 rounded px-2 py-1 text-sm"
          >
            {leaves.map(path => <option key={path} value={path}>{path}</option>)}
          </select>
        </label>
        <label className="text-xs text-gray-700">
          Max records
          <input
            type="number" min={1} value={spec.maxItems}
            onChange={e => patch({ maxItems: Number(e.target.value) })}
            data-testid="sb-max"
            className="mt-1 w-full border border-gray-200 rounded px-2 py-1 text-sm"
          />
        </label>
        <label className="text-xs text-gray-700 col-span-2">
          Description <span className="text-gray-400">(what a reader needs to know — R13)</span>
          <input
            value={spec.description}
            onChange={e => patch({ description: e.target.value })}
            data-testid="sb-description"
            className="mt-1 w-full border border-gray-200 rounded px-2 py-1 text-sm"
          />
        </label>
      </div>

      {/* ADVANCED — the inputs that have a correct answer already.
          The collection property is here rather than on the form because NOTHING SPELLS IT: every
          reader discovers it by lookup (`keyed_collection`: the property carrying `x-key`, or the
          sole array) and no reference contains it — a token reads
          `{{data.<alias>.by_key.<key>.<field>}}`. It is still shown and editable, because an
          existing artifact may need correcting and a derived value nobody can see is worse than
          one nobody needs to change. */}
      <div className="mb-4">
        <button
          type="button"
          onClick={() => setAdvancedOpen(o => !o)}
          data-testid="sb-advanced-toggle"
          aria-expanded={advancedOpen}
          className="text-xs text-gray-500 hover:text-gray-700"
        >
          {advancedOpen ? '▾' : '▸'} Advanced
          {!advancedOpen && (
            <span className="text-gray-400"> — collection property, minimum records</span>
          )}
        </button>
        {advancedOpen && (
          <div className="grid grid-cols-2 gap-3 mt-2" data-testid="sb-advanced">
            <label className="text-xs text-gray-700">
              Collection property
              <input
                value={spec.collection}
                onChange={e => {
                  setOwnedNames(o => ({ ...o, collection: true }))
                  patch({ collection: e.target.value })
                }}
                placeholder="directors"
                data-testid="sb-collection"
                className="mt-1 w-full border border-gray-200 rounded px-2 py-1 text-sm font-mono"
              />
              <span className="text-[10px] text-gray-400">
                The array’s property name inside the schema. Nothing references it by name — a
                reference reads <code>by_key</code>, never this.
              </span>
            </label>
            <label className="text-xs text-gray-700">
              Min records <span className="text-gray-400">(blank = none)</span>
              <input
                type="number" min={0} value={spec.minItems ?? ''}
                onChange={e => patch({
                  minItems: e.target.value === '' ? undefined : Number(e.target.value) })}
                data-testid="sb-min"
                className="mt-1 w-full border border-gray-200 rounded px-2 py-1 text-sm"
              />
              <span className="text-[10px] text-gray-400">
                A minimum forces seed records at creation, because a schema whose own data
                violates its <code>minItems</code> is a draft the validator rejects.
              </span>
            </label>
          </div>
        )}
      </div>

      <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">Fields</h3>
      <table className="w-full text-sm mb-2">
        <tbody>
          {renderFieldRows(spec.fields, [], 0)}
        </tbody>
      </table>
      <button
        type="button"
        onClick={() => patch({
          fields: [...spec.fields, { name: '', type: 'string', required: false }] })}
        data-testid="sb-add-field"
        className="text-xs text-indigo-700 hover:underline mb-4"
      >
        ＋ add field
      </button>

      {/* Offered only once the SHAPE is valid. A record is data ABOUT a schema, so
          inviting rows before the schema can be created asks the operator to fill a
          table whose columns and key are still undecided — and then reports the
          consequences as errors about their rows. */}
      {!shapeReady && (
        <p className="text-xs text-gray-500 mb-3" data-testid="sb-rows-blocked">
          First records open once the schema above is valid — the columns and the key
          field have to be settled before a record can mean anything.
        </p>
      )}
      {shapeReady && (<>
      {/* THE INITIAL RECORDS. Creating a set empty is what made the field picker
          look broken: its concrete `by_key.<key>.<field>` offers come from the REAL
          KEYS in the data, so an empty set offers no field at all and an author had
          to create, promote, fill and promote again before they could reference
          one. Seeding here makes the set referenceable as soon as it deploys. */}
      <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-1">
        First records
      </h3>
      <p className="text-xs text-gray-500 mb-2" data-testid="sb-rows-why">
        A set created empty cannot be referenced yet — the picker offers a field only
        once a record with that key exists. Leave a cell blank to omit it.
      </p>
      {/* Columns are the record's SCALAR LEAVES, spelled the way a consumer spells
          them (`address.city`). A cell cannot hold a subtree and an object is not a
          value, so `address` contributes no column of its own — its leaves do. The
          spreadsheet feel survives; the document underneath is genuinely nested. */}
      <table className="w-full text-sm mb-2">
        <thead>
          <tr>
            {leaves.map(path => (
              <th key={path} className="text-left text-xs font-normal text-gray-400 pr-2">
                {path}{path === spec.keyField ? ' (key)' : ''}
              </th>
            ))}
            <th />
          </tr>
        </thead>
        <tbody>
          {spec.rows.map((row, i) => (
            <tr key={i} data-testid={`sb-row-${i}`}>
              {leaves.map(path => {
                const leaf = leafType(spec.fields, path)
                const value = readPath(row, path)
                return (
                  <td key={path} className="pr-2">
                    {/* Typed at the INPUT, so the value never needs coercing on the
                        way out — a coercion step is where "42" silently becomes a
                        string the schema then rejects. */}
                    {leaf === 'boolean' ? (
                      <input
                        type="checkbox"
                        checked={value === true}
                        onChange={e => patchRow(i, path, e.target.checked)}
                        data-testid={`sb-row-${i}-${path}`}
                      />
                    ) : leaf === 'number' || leaf === 'integer' ? (
                      <input
                        type="number"
                        value={value === undefined ? '' : String(value)}
                        onChange={e => patchRow(i, path,
                          e.target.value === '' ? undefined : Number(e.target.value))}
                        data-testid={`sb-row-${i}-${path}`}
                        className="w-full border border-gray-200 rounded px-2 py-1 text-sm"
                      />
                    ) : (
                      <input
                        value={value === undefined ? '' : String(value)}
                        onChange={e => patchRow(i, path, e.target.value)}
                        data-testid={`sb-row-${i}-${path}`}
                        className="w-full border border-gray-200 rounded px-2 py-1 text-sm"
                      />
                    )}
                  </td>
                )
              })}
              <td>
                {spec.rows.length > 1 && (
                  <button
                    type="button"
                    onClick={() => patch({ rows: spec.rows.filter((_, j) => j !== i) })}
                    data-testid={`sb-row-remove-${i}`}
                    className="text-xs text-gray-400 hover:text-red-700"
                  >
                    remove
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button
        type="button"
        onClick={() => patch({ rows: [...spec.rows, {}] })}
        data-testid="sb-add-row"
        className="text-xs text-indigo-700 hover:underline mb-4"
      >
        ＋ add record
      </button>

      </>)}

      {problems.length > 0 && (
        <ul className="text-sm text-amber-800 mb-3" data-testid="sb-problems">
          {problems.map(p => <li key={p}>{p}</li>)}
        </ul>
      )}

      {draft && (
        <p className="text-xs text-gray-500 mb-3" data-testid="sb-paths">
          <code>{draft.schemaPath}</code> + <code>{draft.dataPath}</code> — proposed together as
          one changeset, because a schema without its data document is invisible here.
        </p>
      )}

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={check}
          disabled={!wellFormed || checking}
          data-testid="sb-check"
          className="px-3 py-1.5 text-sm rounded border border-gray-300 disabled:opacity-40"
        >
          {checking ? 'Checking…' : 'Check'}
        </button>
        <button
          type="button"
          onClick={propose}
          disabled={!canCreate}
          data-testid="sb-create"
          className="px-3 py-1.5 text-sm rounded bg-indigo-600 text-white disabled:opacity-40"
        >
          {proposing ? 'Proposing…' : checking ? 'Validating…' : 'Create'}
        </button>
        {!canPropose && (
          <span className="text-xs text-gray-500">Creating needs the fleet-owner role.</span>
        )}
        {canPropose && wellFormed && verdict?.valid !== true && !checking && !proposing && (
          <span className="text-xs text-gray-500" data-testid="sb-create-will-validate">
            Create runs the canonical validator first — nothing is proposed unless it passes.
          </span>
        )}
      </div>

      {unchecked && (
        <p className="mt-3 text-sm text-amber-800" data-testid="sb-unchecked">
          The draft could not be validated: {unchecked}. <strong>This is not “the draft is
          valid”</strong> — creating is refused rather than guessed, because a published version
          is immutable.
        </p>
      )}
      {verdict?.valid === true && (
        <p className="mt-3 text-sm text-green-700" data-testid="sb-valid">
          The canonical validator accepts this draft.
        </p>
      )}
      {verdict && verdict.valid !== true && (
        <div className="mt-3" data-testid="sb-findings">
          {(draftFindings.length > 0 || verdict.compile_note) && (
            <>
              <p className="text-sm text-red-700">The canonical validator rejects this draft:</p>
              <ul className="text-xs text-red-700">
                {draftFindings.map(l => (
                  <li key={l.key}><code>{l.rel}</code> — {l.text}</li>
                ))}
                {verdict.compile_note && <li>{verdict.compile_note}</li>}
              </ul>
            </>
          )}
          {registryFindings.length > 0 && (
            <div className="mt-2" data-testid="sb-registry-findings">
              <p className="text-sm text-red-700">
                This fleet’s data registry is already invalid in a file this draft does not touch.
                Nothing on this form caused it, and creating stays refused until that file is
                corrected:
              </p>
              <ul className="text-xs text-red-700">
                {registryFindings.map(l => (
                  <li key={l.key}><code>{l.rel}</code> — {l.text}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
      {error && (
        <p className="mt-3 text-sm text-red-700" data-testid="sb-error">{error}</p>
      )}
    </div>
  )
}
